import { useCallback, useMemo, type ReactNode } from 'react'
import { useSearchUsers } from '@front/model'
import { Group, Loader, ScrollArea, Stack, type ScrollAreaProps } from '@mantine/core'
import { isDefined, isEmpty, isNotEmpty, toUserId, type UserSearchInput } from '@terros/common'
import { TButton } from 'app/components/buttons'
import { type PickUserSearchProps } from './PickUserSearch'
import { UserItem } from './UserItem'

type UsersListProps = PickUserSearchProps & {
  query: string
  resultAreaProps?: ScrollAreaProps
  options?: UserSearchInput['options']
  canRemove?: boolean
}

export const UsersList = ({
  onSelect,
  companyId,
  query,
  pickedUserIds,
  resultAreaProps,
  options,
  closerStatus,
  canRemove,
  onRemove,
  disabledUserIds,
  onSelectAll,
  onDeselectAll,
  teamIds,
}: UsersListProps): ReactNode => {
  const { data: users, isLoading, error } = useSearchUsers({ query, companyId, options, closerStatus, teamIds })

  const isAllSelected = useMemo(() => {
    return isNotEmpty(users) && isNotEmpty(pickedUserIds) && users.every((user) => pickedUserIds.includes(user.userId))
  }, [users, pickedUserIds])

  const handleSelectAll = useCallback(() => {
    if (!onSelectAll) return
    if (isEmpty(users)) return
    const canSelectUsers = users.filter((user) => {
      return !pickedUserIds?.includes(user.userId) && !disabledUserIds?.includes(user.userId)
    })
    if (isEmpty(canSelectUsers)) return
    onSelectAll(canSelectUsers.map(toUserId))
  }, [onSelectAll, users, pickedUserIds, disabledUserIds])

  const handleDeselectAll = useCallback(() => {
    if (!onDeselectAll) return
    if (isEmpty(users)) return
    if (!onRemove) return
    const canDeselectUsers = users.filter((user) => {
      return pickedUserIds?.includes(user.userId)
    })
    if (isEmpty(canDeselectUsers)) return

    onDeselectAll(canDeselectUsers.map(toUserId))
  }, [onDeselectAll, users, onRemove, pickedUserIds])

  if (isLoading || error) return <Loader className='self-center' />

  return (
    <ScrollArea {...resultAreaProps}>
      <Stack gap='xs'>
        {isNotEmpty(users) && !isLoading && (
          <Group>
            {isDefined(onSelectAll) && (
              <TButton disabled={isAllSelected} flex={1} variant='secondary' onClick={handleSelectAll}>
                Select All
              </TButton>
            )}
            {isDefined(onDeselectAll) && (
              <TButton flex={1} variant='secondary' onClick={handleDeselectAll}>
                Deselect All
              </TButton>
            )}
          </Group>
        )}
        {users?.map((user) => (
          <UserItem
            canRemove={canRemove}
            disabledUserIds={disabledUserIds}
            key={user.userId}
            pickedUserIds={pickedUserIds}
            user={user}
            onRemove={onRemove}
            onSelect={onSelect}
          />
        ))}
      </Stack>
    </ScrollArea>
  )
}
